/**
 * AuthGuard - Role-based route protection component
 * Protects admin-only routes and redirects unauthorized users
 */

import { useEffect, useState, type ReactNode } from "react";
import { Shield, XCircle } from "lucide-react";
import { authService } from "@/services/authService";
import type { AuthLevel } from "@/types";
import { useDashboardStore } from "@/store";
import { cn } from "@/lib/utils";

interface AuthGuardProps {
  children: ReactNode;
  requireRole?: AuthLevel | AuthLevel[];
  fallback?: ReactNode;
  redirectTo?: string;
}

export function AuthGuard({
  children,
  requireRole,
  fallback,
  redirectTo = "/",
}: AuthGuardProps) {
  const { addLogEntry } = useDashboardStore();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState<string | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      const session = authService.getSession();

      if (!session) {
        // Not logged in
        addLogEntry({
          category: "auth",
          action: "Access Denied",
          details: "User attempted to access protected route without authentication",
          severity: "warning",
          source: "auth-guard",
        });
        setShouldRedirect(redirectTo);
        return;
      }

      // Check role requirements
      if (requireRole) {
        const requiredRoles = Array.isArray(requireRole) ? requireRole : [requireRole];
        const hasRequiredRole = requiredRoles.some((r) => r === session.level);

        if (!hasRequiredRole) {
          addLogEntry({
            category: "auth",
            action: "Access Denied",
            details: `User with role ${session.level} attempted to access ${requiredRoles.join(" or ")} only route`,
            severity: "warning",
            source: "auth-guard",
          });
          setShouldRedirect("/unauthorized");
          return;
        }
      }

      setIsAuthorized(true);
      setIsLoading(false);
    };

    checkAuth();
  }, [requireRole, redirectTo, addLogEntry]);

  // Handle redirect using window.location for simplicity
  useEffect(() => {
    if (shouldRedirect) {
      window.location.href = shouldRedirect;
    }
  }, [shouldRedirect]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[hsl(var(--primary))]" />
      </div>
    );
  }

  if (!isAuthorized) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="flex h-screen items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="flex justify-center">
            <XCircle className="h-16 w-16 text-[hsl(var(--destructive))]" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold">Access Denied</h1>
            <p className="text-[hsl(var(--muted-foreground))]">
              You don't have permission to access this page.
            </p>
          </div>
          <div className="flex justify-center gap-3">
            <button
              onClick={() => window.location.href = redirectTo}
              className="px-4 py-2 rounded-lg bg-[hsl(var(--primary))] text-black font-medium hover:opacity-90 transition-opacity cursor-pointer"
            >
              Go Home
            </button>
            <button
              onClick={() => authService.logout()}
              className="px-4 py-2 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Role badge component for displaying user role
 */
interface RoleBadgeProps {
  role: AuthLevel;
  showIcon?: boolean;
  size?: "sm" | "md" | "lg";
}

export function RoleBadge({ role, showIcon = true, size = "md" }: RoleBadgeProps) {
  const sizeClasses = {
    sm: "text-xs px-2 py-0.5",
    md: "text-sm px-2.5 py-1",
    lg: "text-base px-3 py-1.5",
  };

  const roleConfig = {
    master: {
      label: "Master",
      color: "bg-red-500/10 text-red-500 border-red-500/20",
      icon: Shield,
    },
    creator: {
      label: "Creator",
      color: "bg-purple-500/10 text-purple-500 border-purple-500/20",
      icon: Shield,
    },
    user: {
      label: "User",
      color: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      icon: Shield,
    },
    passkey: {
      label: "Passkey",
      color: "bg-green-500/10 text-green-500 border-green-500/20",
      icon: Shield,
    },
    none: {
      label: "Guest",
      color: "bg-gray-500/10 text-gray-500 border-gray-500/20",
      icon: XCircle,
    },
  };

  const config = roleConfig[role] || roleConfig.none;
  const Icon = config.icon;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border font-medium",
        sizeClasses[size],
        config.color
      )}
    >
      {showIcon && <Icon className="h-3.5 w-3.5" />}
      <span>{config.label}</span>
    </span>
  );
}

/**
 * useAuth hook - Helper for auth-related operations
 */
export function useAuth() {
  const { authLevel } = useDashboardStore();

  const hasRole = useCallback(
    (requiredRole: AuthLevel | AuthLevel[]): boolean => {
      const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
      return roles.some((r) => r === authLevel);
    },
    [authLevel]
  );

  const isAdmin = useCallback(() => hasRole(["master", "creator"]), [hasRole]);
  const isUser = useCallback(() => hasRole("user"), [hasRole]);

  const canAccess = useCallback(
    (resource: string, action: string): boolean => {
      // Simple permission check - can be extended
      if (hasRole(["master", "creator"])) return true;

      // Users have limited access
      const userPermissions: Record<string, string[]> = {
        "agi": ["read", "write"],
        "api-keys": ["read", "create"],
      };

      const allowedActions = userPermissions[resource] || [];
      return allowedActions.includes(action);
    },
    [hasRole]
  );

  return {
    authLevel,
    hasRole,
    isAdmin,
    isUser,
    canAccess,
  };
}

// Import useCallback from React
import { useCallback } from "react";
